import { isEqual } from 'lodash';
import type { ReportMetricsStoreBindings } from '@/views/reports/report-metrics-api';
import { resolveReportSortExclude, buildDedicatedTableDimensions } from '@/views/reports/report-metrics-bundles-static';
import {
  resolveFullMetricHelp,
  resolveMetricsTextByValue,
  resolveShortMetricHelp,
  type MetricHelpContext,
} from '@/views/reports/report-metrics-help-runtime';
import { resolveRouteMetricsDefinition } from '@/views/reports/report-metrics-route-runtime';
import { resolveReportOrderBy } from '@/views/reports/report-metrics-sort-runtime';
import {
  applyMetricColumnHelp,
  buildDedicatedMetricColumns,
  buildDimensionMetricDrafts,
  buildExistingMetricsForRoute,
  buildLockedMainColumnDraft,
  buildTreeLevelGroupingDrafts,
  buildTreeLevelMetricColumns,
  filterDefaultSelectedMetrics,
  filterMetricDraftsByReportExclude,
  getCustomColumnParams as resolveCustomColumnParams,
} from '@/views/reports/report-metrics-columns-runtime';
import {
  getDedicatedReportRouteName,
  isDedicatedReportRoute,
} from '@/views/reports/report-route-behavior';

export type MetricsPickerDedicatedBundles = {
  base: Record<string, unknown>;
  conversions: Record<string, unknown>;
  postbacks: Record<string, unknown>;
  errors: Record<string, unknown>;
  'live-clicks': Record<string, unknown>;
  'live-visits': Record<string, unknown>;
};

export type MetricsPickerStoreBindings = ReportMetricsStoreBindings;

export type CreateMetricsPickerContextOptions = {
  translate: (key: string) => string;
  store: MetricsPickerStoreBindings;
  getTableQueryForCurrentTab: () => Record<string, unknown>;
  getRouteName: () => string | symbol | null | undefined;
  getRouteType: () => string;
  getDedicatedMetricsBundles: () => MetricsPickerDedicatedBundles;
  reportMetricsName: (key: string) => string;
  getGroupingValues: () => unknown[];
  setGroupingValues: (values: unknown[]) => void;
};

export const createMetricsPickerContext = (options: CreateMetricsPickerContextOptions) => {
  const { store, reportMetricsName } = options;

  const getRouteName = () => options.getRouteName();

  const getMetrics = (routeName: string | symbol | null | undefined) => {
    return resolveRouteMetricsDefinition(routeName, options.getDedicatedMetricsBundles());
  };

  const getMetricHelpContext = (routeName: string | symbol | null | undefined): MetricHelpContext => {
    const metrics = getMetrics(routeName) as { metrics_help?: Record<string, { short?: string; full?: string }> };

    return {
      translate: options.translate,
      customEvents: store.getCustomEvents() as Record<string, { slug: string; name: string }>,
      getCustomColumns: () => store.getCustomColumns(),
      metricsHelp: metrics.metrics_help as Record<string, { short?: string; full?: string }>,
      reportMetricsName,
    };
  };

  const getShortMetricHelp = (name: string, routeName = getRouteName()) => {
    return resolveShortMetricHelp(name, getMetricHelpContext(routeName));
  };

  const getFullMetricHelp = (name: string, routeName = getRouteName()) => {
    return resolveFullMetricHelp(name, getMetricHelpContext(routeName));
  };

  const getMetricsTextByValue = (value: string, routeName = getRouteName()) => {
    return resolveMetricsTextByValue(value, getMetricHelpContext(routeName));
  };

  const existingMetricsCustom = (routeName: string | symbol | null | undefined = getRouteName()) => {
    const metrics = getMetrics(routeName) as {
      avaliable_metrics: string[];
      default_include: string[];
    };

    return buildDedicatedMetricColumns({
      availableMetrics: metrics.avaliable_metrics,
      defaultInclude: metrics.default_include,
      reportMetricsName: (name) => reportMetricsName(name),
      getShortHelp: (name) => getShortMetricHelp(name, routeName),
      getFullHelp: (name) => getFullMetricHelp(name, routeName),
    });
  };

  const treeLevelColumns = () => {
    return buildTreeLevelMetricColumns({
      treeLevels: options.getTableQueryForCurrentTab().treeLevels as string[] | undefined,
      dimensions: store.getDimensions() as Record<string, { mainColumn: string }>,
      reportMetricsName,
    });
  };

  const getCustomColumnParams = (name: string) => {
    return resolveCustomColumnParams(name, () => store.getCustomColumns());
  };

  const getExistingMetrics = () => {
    return getExistingMetricsForRoute(getRouteName(), options.getRouteType());
  };

  const applyHelp = (value: unknown[], existedColumns = true) => {
    return applyMetricColumnHelp({
      columns: value as { value: string }[],
      existedColumns,
      resolveIndex: (columnValue) => {
        const metric = getExistingMetrics().find((existingMetric: { value: string; index: number }) => {
          return existingMetric.value === columnValue;
        });
        return metric?.index;
      },
      resolveShortHelp: (columnValue) => getShortMetricHelp(columnValue),
      resolveFullHelp: (columnValue) => getFullMetricHelp(columnValue),
      groupingValueList: options.getGroupingValues().map((item: { value?: string }) => item.value),
      getCustomColumnParams: (columnValue) => getCustomColumnParams(columnValue),
    });
  };

  const getExistingMetricsSimple = (type: string) => {
    const dimensions = store.getDimensions();
    const { additionalColumns = [], mainColumn } = dimensions[type] || {};

    if (!dimensions[type]) {
      return [];
    }

    let result = buildDimensionMetricDrafts({
      additionalColumns,
      metrics: store.getMetrics(),
    });

    const treeColumns = treeLevelColumns();
    const groupingValues = options.getGroupingValues();

    if (!isEqual(groupingValues, treeColumns)) {
      options.setGroupingValues(buildTreeLevelGroupingDrafts(treeColumns));
    }

    const routeMetrics = getMetrics(getRouteName()) as { report_exclude: Record<string, string[]> };

    if (store.getIsHideFilters()) {
      result = filterMetricDraftsByReportExclude(result, type, routeMetrics.report_exclude);
    }

    return applyHelp([
      buildLockedMainColumnDraft(mainColumn),
      ...options.getGroupingValues(),
      ...result,
    ]);
  };

  const getExistingMetricsForRoute = (routeName: string | symbol | null | undefined, type: string) => {
    return buildExistingMetricsForRoute(routeName, type, {
      isDedicatedRoute: (name) => isDedicatedReportRoute(name),
      getDedicatedColumns: (name) => existingMetricsCustom(name),
      getSimpleColumns: (dimensionType) =>
        getExistingMetricsSimple(dimensionType) as ReturnType<typeof existingMetricsCustom>,
    });
  };

  const getInheritColumns = (alternative = false) => {
    return store.getIndependentColumnSettingsEnabled() === alternative;
  };

  const dimensionType = (dimension: string) => {
    return getDedicatedReportRouteName(dimension);
  };

  const findOrderBy = (value: unknown[], dimension = options.getRouteType()) => {
    const metrics = getMetrics(getRouteName()) as { default_sort?: string[] };

    return resolveReportOrderBy({
      values: value,
      defaultSort: metrics.default_sort,
      existingMetrics: getExistingMetricsForRoute(getRouteName(), dimension),
      sortExclude: resolveReportSortExclude(dimensionType(dimension)) || [],
    });
  };

  const getDefaultSelected = (type = options.getRouteType()) => {
    const routeName = getRouteName();
    const { default_include, default_omit } = getMetrics(routeName) as {
      default_include: string[];
      default_omit: string[];
    };

    return filterDefaultSelectedMetrics(
      getExistingMetricsForRoute(routeName, type),
      {
        defaultInclude: default_include,
        defaultOmit: default_omit,
      },
    );
  };

  const tableFullDimensions = () => {
    return buildDedicatedTableDimensions(
      store.getDimensions() as Record<string, unknown>,
    );
  };

  const getCustomMetrics = (routeName: string) => {
    return getMetrics(routeName);
  };

  const getBaseMetrics = () => {
    return options.getDedicatedMetricsBundles().base;
  };

  return {
    getInheritColumns,
    dimensionType,
    findOrderBy,
    getDefaultSelected,
    tableFullDimensions,
    getMetrics,
    getMetricHelpContext,
    existingMetricsCustom,
    getExistingMetricsSimple,
    getExistingMetrics,
    getExistingMetricsForRoute,
    getCustomMetrics,
    getBaseMetrics,
    getMetricsTextByValue,
    getShortMetricHelp,
    getFullMetricHelp,
    treeLevelColumns,
    getCustomColumnParams,
    applyHelp,
    reportMetricsName,
  };
};
